'use client'

import { LANGUAGE_COOKIE_EXPIRY_SECONDS, LANGUAGE_COOKIE_NAME, REGIONS, REGIONS_DATA } from '@/data'
import useCurrentPage from '@/hooks/use-current-page'
import { setCookie } from '@/utils/storage'
import LanguageDropdown from '@/components/LanguageDropdown/LanguageDropdown'

type FooterLanguageSwitcherProps = {
  className?: string
}

// Helper function to group regions by country
function groupRegionsByCountry() {
  const regionsByCountry = new Map<string, { key: string; value: string; data: typeof REGIONS_DATA[keyof typeof REGIONS_DATA] }[]>()

  Object.entries(REGIONS).forEach(([key, value]) => {
    const [, country] = value.split('-')
    if (!country) return // Skip non-country regions like 'en'

    const regionData = REGIONS_DATA[key as keyof typeof REGIONS_DATA]
    if (!regionData) return

    if (!regionsByCountry.has(regionData.countryLabel)) {
      regionsByCountry.set(regionData.countryLabel, [])
    }
    regionsByCountry.get(regionData.countryLabel)?.push({ key, value, data: regionData })
  })

  return Array.from(regionsByCountry.entries()).map(([countryLabel, regions]) => ({
    countryTitle: countryLabel,
    regions,
  }))
}

const FooterLanguageSwitcher = ({ className }: FooterLanguageSwitcherProps) => {
  const { currentLanguage, currentSlug, currentDocType } = useCurrentPage()

  // Ensure we have a valid string slug
  let slug = ''
  if (typeof currentSlug === 'string') {
    slug = currentSlug
  } else if (Array.isArray(currentSlug) && currentSlug.length > 0) {
    slug = currentSlug[0]
  }

  const countryGroups = groupRegionsByCountry()

  const languageGroups = countryGroups.map(group => ({
    countryTitle: group.countryTitle,
    languages: group.regions.map(region => ({
      label: region.data.languageLabel,
      isActive: currentLanguage === region.value,
      languageCode: region.value,
      link: {
        label: region.data.languageLabel,
        linkType: 'internal',
        link: {
          _type: currentDocType || '',
          slug,
        },
        onClick: () => {
          setCookie({ name: LANGUAGE_COOKIE_NAME, value: region.value, maxAge: LANGUAGE_COOKIE_EXPIRY_SECONDS })
        },
      } as SanityLink,
    })),
  }))

  if (!currentLanguage) return null

  // Get the display label for the current language
  const getCurrentLanguageLabel = () => {
    const currentRegionKey = Object.values(REGIONS).find(v => v === currentLanguage)
    return currentRegionKey || ''
  }

  return (
    <>
      <div className={className}>
        <LanguageDropdown
          label={getCurrentLanguageLabel()}
          dropdownPosition="topLeft"
          languageGroups={languageGroups}
        />
      </div>
    </>
  )
}

FooterLanguageSwitcher.displayName = 'FooterLanguageSwitcher'

export default FooterLanguageSwitcher
